/**
 * Token Refresh Interceptor
 *
 * Implements automatic RS256 platform access token refresh with:
 *   - Proactive refresh: triggers 60s before access token expiry
 *   - Singleton refresh promise: at most one in-flight refresh, all
 *     concurrent requests queue behind it
 *   - 401 response handling: retry with new token once; on failure
 *     clear tokens and dispatch auth:logout
 *   - Remember Me awareness: 30d refresh window for guides vs 7d default;
 *     internal staff (2hr) are never extended
 *
 * Platform RS256 tokens only — never handles Cognito or IC tokens.
 *
 * SCRUM-355 | MVP 1.15 V2 Epic 4
 */

// ─── Constants ─────────────────────────────────────────────────────────────────

/** Seconds before expiry at which a proactive refresh is triggered */
export const PROACTIVE_THRESHOLD_SECONDS = 60;

/** Maximum validity windows per user type (seconds) */
export const SESSION_WINDOWS = {
  staff: 2 * 60 * 60,               // 2 hours — no Remember Me
  guide_default: 7 * 24 * 60 * 60,  // 7 days
  guide_remember_me: 30 * 24 * 60 * 60, // 30 days
  customer: 30 * 24 * 60 * 60,      // 30 days
};

// ─── Token Storage ──────────────────────────────────────────────────────────────

/** @returns {string|null} */
export function getAccessToken() {
  return localStorage.getItem('accessToken');
}

/** @returns {string|null} */
export function getRefreshToken() {
  return localStorage.getItem('refreshToken');
}

/** Store rotated token pair after a successful refresh */
function storeTokens(accessToken, refreshToken) {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
}

/** Remove all auth state from local storage */
export function clearTokens() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('rememberMe');
  localStorage.removeItem('tokenIssuedAt');
}

/** Dispatch the auth:logout event so AuthContext clears user state */
export function redirectToLogin() {
  window.dispatchEvent(new CustomEvent('auth:logout', {
    detail: { reason: 'session_expired' },
  }));
}

// ─── JWT Utilities ──────────────────────────────────────────────────────────────

/**
 * Decode a JWT payload without signature verification.
 * @param {string} token
 * @returns {Object|null} decoded payload, or null on malformed input
 */
export function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) { return null; }
    // Convert base64url → base64 and pad to a multiple of 4
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/**
 * Returns true if the access token will expire within PROACTIVE_THRESHOLD_SECONDS.
 * Treats null, malformed, or already-expired tokens as "expiring soon".
 *
 * @param {string|null} accessToken
 * @returns {boolean}
 */
export function isTokenExpiringSoon(accessToken) {
  if (!accessToken) { return true; }
  const payload = decodeJwtPayload(accessToken);
  if (!payload || typeof payload.exp !== 'number') { return true; }
  const expiresInSeconds = payload.exp - Math.floor(Date.now() / 1000);
  return expiresInSeconds < PROACTIVE_THRESHOLD_SECONDS;
}

/**
 * Local-only check: is there a non-expired access token in localStorage?
 * NOT a security boundary — used for business-logic gates (pre-launch)
 * that don't need server-side session validation.
 */
export function isTokenLikelyValid() {
  const token = localStorage.getItem('accessToken');
  if (!token) { return false; }
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') { return false; }
  return payload.exp * 1000 > Date.now();
}

// ─── Session Window ─────────────────────────────────────────────────────────────

/**
 * Derive the expected session window (seconds) from the current access token's
 * user_type/role claim and the stored rememberMe flag.
 *
 * @returns {number} window in seconds
 */
function getSessionWindowSeconds() {
  const rememberMe = localStorage.getItem('rememberMe') === 'true';
  const accessToken = getAccessToken();
  const payload = accessToken ? decodeJwtPayload(accessToken) : null;
  const userType = payload?.user_type || payload?.role || 'customer';

  if (userType === 'staff' || userType === 'admin') {
    // Internal staff have 2-hour sessions; Remember Me never applies
    return SESSION_WINDOWS.staff;
  }

  if (userType === 'guide') {
    return rememberMe ? SESSION_WINDOWS.guide_remember_me : SESSION_WINDOWS.guide_default;
  }

  // customers: always 30 days
  return SESSION_WINDOWS.customer;
}

/**
 * Returns true if we already know the refresh token's validity window has
 * elapsed, allowing us to short-circuit a doomed refresh call.
 *
 * Falls back to false when no `tokenIssuedAt` timestamp is available so that
 * the server makes the authoritative decision.
 *
 * @returns {boolean}
 */
export function isRefreshTokenLikelyExpired() {
  const issuedAt = parseInt(localStorage.getItem('tokenIssuedAt') || '0', 10);
  if (!issuedAt) { return false; }
  const windowSeconds = getSessionWindowSeconds();
  const elapsedSeconds = (Date.now() - issuedAt) / 1000;
  return elapsedSeconds > windowSeconds;
}

// ─── Refresh Logic ──────────────────────────────────────────────────────────────

/** Singleton: at most one in-flight refresh call at a time */
let refreshPromise = null;

/** @returns {string} */
function getApiBaseUrl() {
  return (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL)
    || 'http://localhost:3030';
}

/**
 * Call POST /api/v1/auth/refresh, store the rotated token pair, and dispatch
 * auth:token-refreshed. Throws on network error or non-2xx status.
 *
 * @returns {Promise<string>} new access token
 */
async function performRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await fetch(`${getApiBaseUrl()}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    const err = new Error('Token refresh failed');
    err.status = 401;
    throw err;
  }

  const data = await response.json();
  const { accessToken, refreshToken: newRefreshToken } = data.data;
  storeTokens(accessToken, newRefreshToken);

  window.dispatchEvent(new CustomEvent('auth:token-refreshed'));

  return accessToken;
}

/**
 * Build a new singleton refresh promise.
 * On failure: clears tokens and dispatches auth:logout before re-throwing.
 * On completion (success or failure): resets the singleton.
 *
 * @returns {Promise<string>}
 */
function buildRefreshPromise() {
  return performRefresh()
    .catch(err => {
      clearTokens();
      redirectToLogin();
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });
}

/**
 * Return a valid access token, refreshing proactively if the current token is
 * within PROACTIVE_THRESHOLD_SECONDS of expiry.
 *
 * Concurrent calls during an active refresh all receive the same promise so
 * the refresh endpoint is called exactly once.
 *
 * Returns null when no session exists (unauthenticated request).
 *
 * @returns {Promise<string|null>}
 */
export async function getValidAccessToken() {
  const current = getAccessToken();

  // No session — return null so the caller proceeds without an auth header
  if (!current) { return null; }

  // Token is healthy — no refresh needed
  if (!isTokenExpiringSoon(current)) { return current; }

  // Token is expiring — need a refresh token to continue
  const storedRefreshToken = getRefreshToken();
  if (!storedRefreshToken) {
    clearTokens();
    redirectToLogin();
    const err = new Error('Session expired. Please login again.');
    err.status = 401;
    throw err;
  }

  // Singleton: all concurrent expiring-token requests wait on one promise
  if (!refreshPromise) {
    refreshPromise = buildRefreshPromise();
  }

  return refreshPromise;
}

/**
 * Force a token refresh, used by the 401 response handler.
 *
 * Short-circuits to logout when the refresh token's expected validity window
 * has already elapsed (avoids a futile network call).
 *
 * Shares the singleton promise, so N concurrent 401s still trigger only one
 * refresh call.
 *
 * @returns {Promise<string>} new access token
 */
export async function forceRefresh() {
  const storedRefreshToken = getRefreshToken();
  if (!storedRefreshToken || isRefreshTokenLikelyExpired()) {
    clearTokens();
    redirectToLogin();
    const err = new Error('Session expired. Please login again.');
    err.status = 401;
    throw err;
  }

  if (!refreshPromise) {
    refreshPromise = buildRefreshPromise();
  }

  return refreshPromise;
}

// ─── Initialization ─────────────────────────────────────────────────────────────

/**
 * Call once at app startup to signal that the token refresh interceptor is
 * active. For this fetch-based client the interceptor logic is integrated
 * directly into apiRequest via module imports; this function exists as an
 * explicit, testable initialization hook.
 */
export function setupTokenRefreshInterceptor() {
  // Integrated at module level — no runtime wiring needed
}

/**
 * Reset singleton state. For testing only.
 */
export function _resetInterceptorState() {
  refreshPromise = null;
}
